import primitives from '@veneer/primitives'
import { extendSemantics } from '@veneer/semantics'
import { ThemeProvider as VeneerThemeProvider } from '@veneer/theme'
import React from 'react'
import { useSettings, useSignupSession, useSignupState } from '../../../hooks'
import { resolveThemeMode } from '../../../lib/themeMode'
import { hpxTokens } from './hpxTokens'

export const DEFAULT_MODE = 'light'
export const MODE = DEFAULT_MODE
export const DENSITY = 'standard'

export const shape = (isHpx: boolean) => {
  return isHpx ? 'pill' : 'round'
}

export const customSemantics = (isHpx: boolean) => {
  const HpxTheme = extendSemantics({
    color: {
      light: {
        background: {
          disabled: hpxTokens.royalBlue3, // disabled primary background
          default: hpxTokens.background
        },
        foreground: {
          disabled: primitives.color.gray5 // disabled primary text and border, also disabled secondary text and border
          // disabled: hpxTokens.royalBlue4 // gets correct disabled secondary button, but cannot see text as well in disabled primary button
        },
        primary: {
          foreground: {
            default: primitives.color.white // primary text
          },
          base: {
            default: hpxTokens.royalBlue7, // primary background
            hover: hpxTokens.royalBlue8, // primary hover, secondary border and font
            active: hpxTokens.royalBlue9 // primary click
          },
          background: {
            hover: hpxTokens.background, // secondary background hover
            active: hpxTokens.royalBlue3 // secondary background click
          }
        }
      }
    }
  })

  return isHpx ? HpxTheme : extendSemantics({})
}

export const LayoutTheme = ({ children }) => {
  const { isHpx } = useSignupSession()
  const settingsState = useSettings()
  const signupState = useSignupState()

  const enableDarkModeSignup = settingsState.data?.enableDarkModeSignup
  const appMode = signupState.onboarding?.sessionContext?.app?.mode

  const resolvedMode = resolveThemeMode(enableDarkModeSignup, appMode)
  const isStyleguide = !!(globalThis as any).__STYLEGUIDE__
  const [mode, setMode] = React.useState<'light' | 'dark'>(
    () =>
      (isStyleguide &&
        (sessionStorage.getItem('styleguide-theme') as 'light' | 'dark')) ||
      resolvedMode
  )

  React.useEffect(() => {
    if (!isStyleguide) return
    const handler = () => {
      setMode(
        (sessionStorage.getItem('styleguide-theme') as 'light' | 'dark') ||
          resolvedMode
      )
    }
    globalThis.addEventListener('styleguide-theme-change', handler)
    return () =>
      globalThis.removeEventListener('styleguide-theme-change', handler)
  }, [isStyleguide, resolvedMode])

  return (
    <div className="layouttheme">
      <VeneerThemeProvider
        customSemantics={customSemantics(isHpx)}
        mode={mode}
        shape={shape(isHpx)}
        density={DENSITY}
      >
        {children}
      </VeneerThemeProvider>
    </div>
  )
}
